import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DetailDisplay, ErrorMessage } from "widgets";
import { useFetchCustomerOrderDetails } from "store/backend/assets";
import { formatMonetaryAmount } from "utils/formatMonetaryAmount";
import { formatPhoneWithOptionalIsd } from "utils/formatPhone";
import {
  getOrderCurrencyCode,
  getOrderPendingAmount,
} from "utils/countryMappings";
import { buildOrderDetailsPath } from "constants/omsRegionConfig";
import { UI_LABELS } from "constants/uiLabels";
import { ERROR_MESSAGES } from "constants/errorMessages";

import styles from "./CustomerOrders.module.scss";

import Loader from "shared/Loader/Loader";

const formatOrderCurrency = (amount, order) =>
  formatMonetaryAmount(amount, getOrderCurrencyCode(order));

const handleOnClickPendingOrder = (orderId, navigate, searchParams) => {
  navigate(buildOrderDetailsPath(orderId, searchParams));
};

const CustomerOrders = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cashierUamId] = useState(localStorage.getItem("cashierUamId"));

  const customerId = searchParams.get("customerId");

  const {
    isPending: isOrderDetailsLoading,
    data: orderDetails,
    error: orderDetailsError,
    isError: isErrorFetchingOrderDetails,
  } = useFetchCustomerOrderDetails(customerId);

  if (isOrderDetailsLoading) {
    return <Loader message="Fetching customer data..." />;
  }

  if (isErrorFetchingOrderDetails) {
    return <ErrorMessage>{orderDetailsError.message}</ErrorMessage>;
  }

  if (Number(cashierUamId) === Number(customerId)) {
    return <ErrorMessage>{ERROR_MESSAGES.SELF_LOOKUP_BLOCKED}</ErrorMessage>;
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.cardsContainer}>
        <div className={styles.heading}>Pending Customer Orders</div>
        {orderDetails?.length > 0 ? (
          orderDetails.map((order) => (
            <div
              className={styles.card}
              key={order.order_id}
              onClick={() =>
                handleOnClickPendingOrder(order.order_id, navigate, searchParams)
              }
            >
              <DetailDisplay
                title={UI_LABELS.ORDER_ID}
                value={order.order_id}
              />
              {order.application_number != null && (
                <DetailDisplay
                  title={UI_LABELS.APPLICATION_NO}
                  value={order.application_number}
                />
              )}
              <DetailDisplay
                title={UI_LABELS.BILLING_NAME}
                value={order.address_details.billing_address.recipient_name}
              />
              <DetailDisplay
                title={UI_LABELS.BILLING_PHONE_NUMBER}
                value={formatPhoneWithOptionalIsd(
                  order.address_details.billing_address.masked_phone_no,
                  order.address_details.billing_address.isd_code,
                )}
              />
              <DetailDisplay
                title={UI_LABELS.TOTAL_ORDER_AMOUNT}
                value={formatOrderCurrency(order.amount, order)}
              />
              <DetailDisplay
                title={UI_LABELS.PENDING_AMOUNT}
                value={formatOrderCurrency(getOrderPendingAmount(order), order)}
              />
              <DetailDisplay
                title={UI_LABELS.ORDER_PUNCH_DATE}
                value={new Date(order.order_created_date).toLocaleDateString()}
              />
            </div>
          ))
        ) : (
          <ErrorMessage>{ERROR_MESSAGES.NO_PENDING_ORDERS}</ErrorMessage>
        )}
      </div>
    </div>
  );
};

export default CustomerOrders;
